import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID } from '../../../contracts/heidi-2';
import { workHomeHref } from '../../../contracts/projects/work-home-url';
import {
  computeShouldShowHeidi2ExploreLaterBanner,
  computeShouldShowHeidi2GetStartedChecklist,
  getHeidi2Ports,
  heidi2ExploreLaterRestartAtom,
  useHeidi2ExploreLaterState,
} from '../../../data/heidi-2';
import {
  Heidi2GetStartedChecklist,
  type Heidi2GetStartedChecklistItem,
  type Heidi2GetStartedChecklistItemId,
} from '../../onboarding/Heidi2GetStartedChecklist';
import { heidi2OnboardingLaunchAtom } from '../heidi-2-onboarding-launch-atom';
import { heidi2NewUserHomeOnboardingBannerEligibleAtom } from '../new-user-home-onboarding-banner/heidi-2-new-user-home-onboarding-banner-eligible-atom';

import type { Heidi2GetStartedChecklistPorts } from './heidi-2-get-started-checklist-ports';
import { useHeidi2GetStartedProgress } from './use-heidi-2-get-started-progress';

/**
 * Wires the Heidi II "Get started" checklist: who sees it, what is ticked and
 * where each item goes. Mounted at the foot of the Home panel. Reads billing
 * through the explore-later state, so cloud builds only.
 */
export const Heidi2GetStartedChecklistHost = ({
  ports,
}: {
  ports: Heidi2GetStartedChecklistPorts;
}) => (
  <Heidi2GetStartedChecklistHostContent
    key={useAtomValue(heidi2ExploreLaterRestartAtom)}
    ports={ports}
  />
);

const Heidi2GetStartedChecklistHostContent = ({
  ports,
}: {
  ports: Heidi2GetStartedChecklistPorts;
}) => {
  const appPorts = getHeidi2Ports();
  const state = useHeidi2ExploreLaterState();
  const isClinicalRecordingActive = appPorts.useIsClinicalRecordingActive(false);
  const isExploreLaterBannerVisible = computeShouldShowHeidi2ExploreLaterBanner({
    ...state,
    isClinicalRecordingActive,
  });
  const isCompUsageBannerEligible = useAtomValue(
    heidi2NewUserHomeOnboardingBannerEligibleAtom
  );
  const shouldFoldByDefault =
    !state.isEvidenceTourClosed ||
    isExploreLaterBannerVisible ||
    isCompUsageBannerEligible;
  const { handleNewChat } = appPorts.useChatSidebarHandlers();
  const navigate = appPorts.useNavigate();
  const setLaunch = useSetAtom(heidi2OnboardingLaunchAtom);
  const markModalAsViewed = appPorts.useMarkModalAsViewed();
  const bookDemo = ports.useHeidi2BookDemoPort();
  const [isCollapsed, setIsCollapsed] = useState(shouldFoldByDefault);
  const [appliedFoldDefault, setAppliedFoldDefault] = useState(shouldFoldByDefault);
  // Starts folded while the Evidence tour is open, while the explore-later
  // banner is showing, and while the complimentary-usage banner is eligible,
  // so those surfaces are not open at once. The clinician
  // can still expand it; this only resets when one of those surfaces changes.
  // Adjusted during render, not in an effect: the user query usually settles
  // in the same render that first shows the checklist, and an effect would
  // paint it open for a frame before folding it.
  if (appliedFoldDefault !== shouldFoldByDefault) {
    setAppliedFoldDefault(shouldFoldByDefault);
    setIsCollapsed(shouldFoldByDefault);
  }
  const [isHiddenLocally, setIsHiddenLocally] = useState(false);
  const viewTrackedRef = useRef(false);
  const trackButtonClicked = appPorts.trackButtonClicked;
  const trackChecklistViewed = appPorts.trackHeidi2GetStartedChecklistViewed;
  const connectAppsHref = appPorts.connectAppsHref;

  // Latched, deliberately. The gate reads the user query, and anything that
  // drops that query out of `isSuccess` blanks `useCurrentUserQueryResult`'s
  // data — which reads as `welcomeOutcome: 'unseen'` and used to unmount the
  // checklist mid-session, taking an open Book a demo dialog down with it.
  // Eligibility is settled when the checklist first appears; the X still hides
  // it, and a reload re-decides.
  const isEligible = computeShouldShowHeidi2GetStartedChecklist(state);
  const wasEligibleRef = useRef(false);
  if (isEligible) wasEligibleRef.current = true;
  const isVisible = !isHiddenLocally && wasEligibleRef.current;
  const progress = useHeidi2GetStartedProgress(ports, isVisible, state.welcomeSeenAt);

  useEffect(() => {
    if (!isVisible || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackChecklistViewed();
  }, [isVisible, trackChecklistViewed]);

  const handleSelect = useCallback(
    (id: Heidi2GetStartedChecklistItemId) => {
      trackButtonClicked({
        screen_name: ScreenName.Heidi2GetStartedChecklist,
        action: ButtonAction.Heidi2GetStartedChecklistItem,
        label: id,
      });

      switch (id) {
        case 'welcome-tour':
          setLaunch({ startAt: 'handoff' });
          return;
        case 'give-task':
          // New chat's landing: Home with the composer focused, which opens
          // its suggested work.
          handleNewChat();
          return;
        case 'connect-apps':
          navigate(connectAppsHref);
          return;
        case 'run-routine':
          navigate(workHomeHref({ view: 'skills' }), { shallow: true });
          return;
      }
    },
    [connectAppsHref, handleNewChat, navigate, setLaunch, trackButtonClicked]
  );

  const handleClose = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2GetStartedChecklist,
      action: ButtonAction.Heidi2GetStartedChecklistDismiss,
      label: 'close',
    });
    setIsHiddenLocally(true);
    markModalAsViewed({
      modal_id: HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
      increment_count: false,
      action: ModalViewAction.dismiss,
    });
  }, [markModalAsViewed, trackButtonClicked]);

  const handleBookDemoOpen = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2GetStartedChecklist,
      action: ButtonAction.Heidi2GetStartedChecklistBookDemo,
      label: 'book_demo',
    });
  }, [trackButtonClicked]);

  if (!isVisible) return null;

  const items: Heidi2GetStartedChecklistItem[] = [
    { id: 'welcome-tour', done: state.welcomeOutcome === 'completed' },
    { id: 'give-task', done: progress.hasGivenTask },
    { id: 'connect-apps', done: progress.hasConnectedApp },
    { id: 'run-routine', done: progress.hasRunRoutine },
  ];

  return (
    <div className="flex max-h-[60%] min-h-0 shrink-0 flex-col py-2">
      <Heidi2GetStartedChecklist
        items={items}
        onSelect={handleSelect}
        onClose={handleClose}
        bookDemo={bookDemo}
        onBookDemoOpen={handleBookDemoOpen}
        isCollapsed={isCollapsed}
        onCollapsedChange={setIsCollapsed}
      />
    </div>
  );
};
